import { useMemo } from 'react';

import type { BadgeColor } from '@/lib/base/badges/badges';
import { Badge } from '@/lib/base/badges/badges';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

import type { SalesforceImportJob } from '../types';

interface SalesforceImportProgressProps {
  importJobs?: SalesforceImportJob[];
}

// Known Salesforce import job types → human-readable labels. Unknown types
// fall back to a title-cased rendering of the raw job_type string.
// eslint-disable-next-line react-refresh/only-export-components -- label map constant co-located with the component that uses it
export const JOB_TYPE_LABELS: Record<string, string> = {
  salesforce_accounts: 'Accounts',
  salesforce_contacts: 'Contacts',
  salesforce_opportunities: 'Opportunities',
};

const STATUS_BADGE_COLOR: Record<
  SalesforceImportJob['status'],
  BadgeColor<'pill-color'>
> = {
  active: 'blue',
  completed: 'success',
  failed: 'error',
};

type AggregateState = 'failed' | 'active' | 'completed' | 'preparing';

function humanizeJobType(jobType: string): string {
  const known = JOB_TYPE_LABELS[jobType];
  if (known) {
    return known;
  }
  return jobType
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function computeAggregateState(jobs: SalesforceImportJob[]): AggregateState {
  if (jobs.length === 0) {
    return 'preparing';
  }
  if (jobs.some(job => job.status === 'failed')) {
    return 'failed';
  }
  if (jobs.some(job => job.status === 'active')) {
    return 'active';
  }
  if (jobs.every(job => job.status === 'completed')) {
    return 'completed';
  }
  return 'preparing';
}

function JobRow({ job }: { job: SalesforceImportJob }) {
  const label = humanizeJobType(job.job_type);
  return (
    <li className='flex items-center justify-between gap-3 py-1.5'>
      <span className='text-body-small text-[var(--color-text-primary)]'>
        {label}
      </span>
      <div className='flex items-center gap-2'>
        <span className='text-body-small text-[var(--color-text-secondary)]'>
          {job.items_processed} processed
          {job.items_failed > 0 ? `, ${job.items_failed} failed` : ''}
        </span>
        <Badge type='pill-color' color={STATUS_BADGE_COLOR[job.status]}>
          {job.status}
        </Badge>
      </div>
    </li>
  );
}

/**
 * Surfaces the state of an in-flight (or completed) Salesforce record
 * import. Failure always takes precedence over progress — a failed job
 * must never be masked by an indefinitely-spinning loader.
 */
export function SalesforceImportProgress({
  importJobs,
}: SalesforceImportProgressProps) {
  const jobs = useMemo(() => importJobs ?? [], [importJobs]);
  const aggregateState = useMemo(() => computeAggregateState(jobs), [jobs]);

  return (
    <div className='space-y-3'>
      {aggregateState === 'failed' && (
        <div className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
          <p className='text-body-small font-medium text-utility-error-900'>
            Some records couldn’t be imported
          </p>
          <p className='mt-1 text-body-small text-utility-error-700'>
            Try reconnecting Salesforce, or contact support if this keeps
            happening.
          </p>
        </div>
      )}

      {aggregateState === 'active' && (
        <LoadingIndicator
          type='line-simple'
          size='xs'
          inline
          label='Importing your Salesforce records…'
        />
      )}

      {aggregateState === 'completed' && (
        <Badge type='pill-color' color='success'>
          Import complete
        </Badge>
      )}

      {aggregateState === 'preparing' && (
        <p className='text-body-small text-[var(--color-text-secondary)]'>
          Preparing import…
        </p>
      )}

      {jobs.length > 0 && (
        <ul className='divide-y divide-[var(--color-border-secondary)]'>
          {jobs.map(job => (
            <JobRow key={job.job_type} job={job} />
          ))}
        </ul>
      )}
    </div>
  );
}
